// import { Alert } from "@mui/material";
import { Chip, LinearProgress, Skeleton } from "@mui/material";
import { motion } from "motion/react";
import { ReactNode, useMemo, useState } from "react";
import { TbUserSearch, TbUsersGroup } from "react-icons/tb";
import { useDebounce } from "use-debounce";

import CommonPopup from "@/components/CommonPopup";
import { hasNumber } from "@/helpers/regex";
import { useGetPassengerSearch } from "@/requests/passengers.queries";
import { useMetasActions, useMetasShowGlobalSearch } from "@/stores/use-metas";

import GlobalSearchDialogSkeleton from "./GlobalSearchDialogSkeleton";
import GlobalSearchForm from "./GlobalSearchForm";
import GlobalSearchItemContent from "./GlobalSearchItemContent";
import GlobalSearchStatusCard, { SearchStatuses } from "./GlobalSearchStatusCard";

const getPassengerSearchBody = (text: string): { name?: string; flightNumber?: string } => {
    if (!text) return {};
    const trimmedText = text.trim();
    if (trimmedText.length < 3) return {};
    const isFlightNumber = hasNumber(trimmedText);
    return isFlightNumber ? { flightNumber: trimmedText } : { name: trimmedText };
};

function GlobalSearchDialog() {
    const { setShowGlobalSearch } = useMetasActions();
    const showGlobalSearch = useMetasShowGlobalSearch();

    return (
        <CommonPopup
            title={"Yolcu Arama"}
            icon={TbUserSearch}
            open={showGlobalSearch}
            size="sm"
            onClose={() => setShowGlobalSearch(false)}
        >
            <div className="px-4  pb-4 space-y-6 relative">
                <GlobalSearchDialogContent />
            </div>
        </CommonPopup>
    );
}
function GlobalSearchDialogContent() {
    const [searchText, setSearchText] = useState("");
    const [debouncedSearchText] = useDebounce(searchText, 500);
    const searchBody = useMemo(() => getPassengerSearchBody(debouncedSearchText), [debouncedSearchText]);
    const { data, isPending, isFetching, isError } = useGetPassengerSearch(searchBody);
    const items = data || [];
    return (
        <>
            {isFetching && <LinearProgress className="!absolute !top-0 !rounded-full !left-0 !w-full !m-0" />}
            <GlobalSearchForm text={searchText} onChange={setSearchText}>
                {(searchBody.flightNumber || searchBody.name) && (
                    <div className="absolute top-full -mt-2 right-0 max-w-fit  right-0 z-50 bg-gray-100">
                        {isFetching ? (
                            <Skeleton variant="rounded" width={120} height={22} className="!rounded-full" />
                        ) : (
                            <Chip size="small" icon={<TbUsersGroup />} label={`${items.length} Sonuç bulundu`} />
                        )}
                    </div>
                )}
            </GlobalSearchForm>

            {!searchBody.flightNumber && !searchBody.name && isPending ? (
                <GlobalSearchStatusCard variant={SearchStatuses.INITIAL} />
            ) : isError ? (
                <GlobalSearchStatusCard variant={SearchStatuses.ERROR} />
            ) : items.length ? (
                <GlobalSearchDialogWrapper>
                    <GlobalSearchItemContent
                        items={items}
                        highlightName={searchBody.name}
                        highlightFlightNumber={searchBody.flightNumber}
                    />
                </GlobalSearchDialogWrapper>
            ) : isPending ? (
                <GlobalSearchDialogWrapper>
                    <GlobalSearchDialogSkeleton />
                </GlobalSearchDialogWrapper>
            ) : (
                <GlobalSearchStatusCard variant={SearchStatuses.EMPTY} />
            )}
        </>
    );
}
function GlobalSearchDialogWrapper({ children }: { children: ReactNode }) {
    return (
        <motion.div
            className="space-y-4 "
            initial="hidden"
            animate="visible"
            variants={{
                visible: {
                    transition: {
                        staggerChildren: 0.1,
                    },
                },
            }}
        >
            {children}
        </motion.div>
    );
}

export default GlobalSearchDialog;
